import * as React from "react";
import { Info } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Modal } from "../../../ui/components/Modal";
import { EmailInput } from "../../../ui/components/Form";
import type { OrganizationScopedRole } from "../../lib/api/users";
import type { OrgCertEnrollRole } from "../../lib/api/certifications-org";
import {
  fetchProgressClaims,
  type ProgressClaim,
} from "../../lib/api/certifications";
import * as userApi from "../../lib/api/users";
import { getApiErrorMessage } from "../../lib/api/api-error";
import { useI18n } from "../../lib/i18n";
import { MigrationPreviewModal } from "../system/MigrationPreviewModal";
import { vars } from "../../../ui/styles/theme.css";
import * as confirmStyles from "./OrganizationDetailPage.css";

const INVITE_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type EnrollRoleOption = {
  id: string;
  name: string;
  label?: string | null;
  systemKey?: string | null;
};

function formatRoleLabel(role: string): string {
  return role
    .replace(/^pro:/, "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isValidInviteEmail(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length > 0 && INVITE_EMAIL_PATTERN.test(trimmed);
}

function isAdminRole(role: {
  name?: string | null;
  systemKey?: string | null;
}): boolean {
  return (
    role.systemKey === "admin" ||
    role.name === "admin" ||
    role.name === "pro:admin"
  );
}

function isTechnicianRole(role: { name?: string | null }): boolean {
  return role.name === "technician" || role.name === "pro:technician";
}

function roleCanEnroll(role: OrganizationScopedRole): boolean {
  const perms = role.permissions ?? [];
  return (
    perms.includes("certification:enroll") &&
    perms.includes("certification:take-quiz")
  );
}

/** Prefer Technician, then any non-admin role — never default to Admin. */
function pickDefaultEnrollRoleId(roles: EnrollRoleOption[]): string {
  if (roles.length === 0) return "";
  const technician = roles.find(isTechnicianRole);
  if (technician) return technician.id;
  const nonAdmin = roles.find((r) => !isAdminRole(r));
  if (nonAdmin) return nonAdmin.id;
  return roles[0].id;
}

function sortEnrollRoles(roles: EnrollRoleOption[]): EnrollRoleOption[] {
  return [...roles].sort((a, b) => {
    const aTech = isTechnicianRole(a) ? 0 : 1;
    const bTech = isTechnicianRole(b) ? 0 : 1;
    if (aTech !== bTech) return aTech - bTech;
    const aAdmin = isAdminRole(a) ? 1 : 0;
    const bAdmin = isAdminRole(b) ? 1 : 0;
    if (aAdmin !== bAdmin) return aAdmin - bAdmin;
    const aLabel = (a.label?.trim() || formatRoleLabel(a.name)).toLowerCase();
    const bLabel = (b.label?.trim() || formatRoleLabel(b.name)).toLowerCase();
    return aLabel.localeCompare(bLabel);
  });
}

function ClaimInfoIcon({ title }: { title: string }) {
  return (
    <span
      title={title}
      aria-label={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        color: vars.color.fgMuted,
        cursor: "help",
      }}
    >
      <Info size={14} aria-hidden />
    </span>
  );
}

export function InviteToEnrollModal({
  open,
  setOpen,
  orgId,
  orgName,
  token,
  roles,
  rolesLoading,
  enrollRoleFallback,
  onInvited,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  orgId: string;
  orgName: string;
  token: string | null;
  roles: OrganizationScopedRole[];
  rolesLoading: boolean;
  /** Used when org role payloads omit permissions. */
  enrollRoleFallback?: OrgCertEnrollRole[];
  onInvited?: () => void;
}) {
  const { t } = useI18n();
  const enrollRoles = React.useMemo((): EnrollRoleOption[] => {
    const byPerms = roles.filter(roleCanEnroll).map((r) => ({
      id: r.id,
      name: r.name,
      label: r.label,
      systemKey: r.systemKey,
    }));
    const raw =
      byPerms.length > 0
        ? byPerms
        : (enrollRoleFallback ?? []).map((r) => ({
            id: r.id,
            name: r.name,
            label: r.label,
            systemKey: null as string | null,
          }));
    return sortEnrollRoles(raw);
  }, [roles, enrollRoleFallback]);

  const [email, setEmail] = React.useState("");
  const [roleId, setRoleId] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [showEmailValidation, setShowEmailValidation] = React.useState(false);
  const [adminConfirmOpen, setAdminConfirmOpen] = React.useState(false);
  const [suggestOpen, setSuggestOpen] = React.useState(false);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewClaimId, setPreviewClaimId] = React.useState<string | null>(
    null,
  );
  const [debouncedSearch, setDebouncedSearch] = React.useState("");

  React.useEffect(() => {
    if (!open) {
      setEmail("");
      setRoleId("");
      setError(null);
      setSuccess(null);
      setLoading(false);
      setShowEmailValidation(false);
      setAdminConfirmOpen(false);
      setSuggestOpen(false);
      setPreviewOpen(false);
      setPreviewClaimId(null);
      setDebouncedSearch("");
      return;
    }
    if (!roleId && enrollRoles.length > 0) {
      setRoleId(pickDefaultEnrollRoleId(enrollRoles));
    }
  }, [open, enrollRoles, roleId]);

  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(email.trim().toLowerCase());
    }, 250);
    return () => window.clearTimeout(handle);
  }, [email]);

  const claimsSuggestQuery = useQuery({
    queryKey: ["invite-enroll-claim-suggest", debouncedSearch],
    queryFn: (): Promise<ProgressClaim[]> =>
      fetchProgressClaims(token!, {
        status: "all",
        search: debouncedSearch,
      }),
    enabled: Boolean(open && token && debouncedSearch.length >= 2),
    staleTime: 30_000,
  });

  const suggestions = React.useMemo(() => {
    const rows = claimsSuggestQuery.data ?? [];
    const q = debouncedSearch;
    if (!q) return [];
    return rows
      .filter((c) => c.email.toLowerCase().includes(q))
      .slice(0, 8);
  }, [claimsSuggestQuery.data, debouncedSearch]);

  const exactClaim = React.useMemo(() => {
    const normalized = email.trim().toLowerCase();
    if (!isValidInviteEmail(email)) return null;
    const fromSuggest = (claimsSuggestQuery.data ?? []).find(
      (c) => c.email.toLowerCase() === normalized,
    );
    return fromSuggest ?? null;
  }, [claimsSuggestQuery.data, email]);

  const selectedRole = enrollRoles.find((r) => r.id === roleId) ?? null;
  const selectedIsAdmin = selectedRole ? isAdminRole(selectedRole) : false;
  const selectedRoleLabel = selectedRole
    ? selectedRole.label?.trim() || formatRoleLabel(selectedRole.name)
    : "Admin";

  const trimmedEmail = email.trim();
  const emailValid = isValidInviteEmail(email);
  const emailFieldError =
    showEmailValidation && trimmedEmail.length > 0 && !emailValid
      ? "Please enter a valid email address (e.g. name@company.com)"
      : undefined;
  const canInvite =
    Boolean(token) && emailValid && Boolean(roleId) && enrollRoles.length > 0;

  const claimTooltip = t("pro.orgs.invite.claimTooltip");

  const sendInvite = async () => {
    if (!token) {
      setError("Session expired. Please sign in again.");
      return;
    }
    if (!emailValid) {
      setShowEmailValidation(true);
      setError("Please enter a valid email address (e.g. name@company.com)");
      return;
    }
    if (!roleId) {
      setError("Select a role");
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await userApi.createOrganizationInvite(token, orgId, {
        email: trimmedEmail,
        roleId,
        intent: "certification",
      });
      setSuccess(`Certification invite sent to ${trimmedEmail}`);
      setEmail("");
      onInvited?.();
      setShowEmailValidation(false);
      setAdminConfirmOpen(false);
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to send invitation"));
      setAdminConfirmOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteClick = () => {
    if (!canInvite) {
      if (!emailValid) {
        setShowEmailValidation(true);
        setError("Please enter a valid email address (e.g. name@company.com)");
      } else if (!roleId) {
        setError("Select a role");
      }
      return;
    }
    if (selectedIsAdmin) {
      setError(null);
      setAdminConfirmOpen(true);
      return;
    }
    void sendInvite();
  };

  return (
    <>
      <Modal
        open={open}
        setOpen={setOpen}
        minWidth="480px"
        maxWidth="520px"
        minHeight="auto"
      >
        <div className={confirmStyles.confirmModalBody}>
          <h3 className={confirmStyles.confirmModalTitle}>{t("pro.orgs.inviteToEnroll.title")}</h3>
          <p className={confirmStyles.confirmModalText}>
            Invite someone to join <strong>{orgName}</strong> with a role that
            can enroll in certifications. They will receive an email with a link
            to accept.
          </p>

          {error && <Callout variant="error">{error}</Callout>}
          {success && <Callout variant="success">{success}</Callout>}

          <div style={{ position: "relative" }}>
            <EmailInput
              label={t("pro.orgs.emailAddress")}
              placeholder="colleague@company.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setShowEmailValidation(false);
                setSuggestOpen(true);
                if (error) setError(null);
              }}
              onFocus={() => setSuggestOpen(true)}
              onBlur={() => {
                setShowEmailValidation(true);
                window.setTimeout(() => setSuggestOpen(false), 150);
              }}
              error={emailFieldError}
              autoComplete="email"
              disabled={loading}
            />
            {suggestOpen && suggestions.length > 0 && (
              <ul
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: "100%",
                  zIndex: 20,
                  margin: "4px 0 0",
                  padding: 4,
                  listStyle: "none",
                  background: vars.color.card,
                  border: `1px solid ${vars.color.border}`,
                  borderRadius: 8,
                  boxShadow: "0 8px 20px -8px rgba(0,0,0,0.25)",
                  maxHeight: 200,
                  overflow: "auto",
                }}
              >
                {suggestions.map((claim) => (
                  <li key={claim.id}>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setEmail(claim.email);
                        setSuggestOpen(false);
                      }}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "8px 10px",
                        border: "none",
                        borderRadius: 6,
                        background: "transparent",
                        cursor: "pointer",
                        font: "inherit",
                        color: vars.color.fg,
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{claim.email}</div>
                      <div
                        style={{
                          fontSize: 12,
                          color: vars.color.fgMuted,
                          marginTop: 2,
                        }}
                      >
                        {claim.moduleSlugs.join(", ") || "Legacy claim"}
                        {claim.claimedAt ? " · claimed" : " · pending"}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {emailValid && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                padding: 12,
                borderRadius: 8,
                border: `1px solid ${vars.color.border}`,
                background: vars.color.muted,
              }}
            >
              {exactClaim ? (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    {t("pro.orgs.invite.claimAvailable")}
                    <ClaimInfoIcon title={claimTooltip} />
                  </div>
                  <div style={{ fontSize: 12, color: vars.color.fgMuted }}>
                    {exactClaim.moduleCount} modules / {exactClaim.trainingCount}{" "}
                    trainings
                    {exactClaim.moduleSlugs.length
                      ? ` · ${exactClaim.moduleSlugs.join(", ")}`
                      : ""}
                    {exactClaim.claimedAt ? " · already claimed" : ""}
                  </div>
                  {!exactClaim.claimedAt ? (
                    <div>
                      <Button
                        color="neutral"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setPreviewClaimId(exactClaim.id);
                          setPreviewOpen(true);
                        }}
                      >
                        {t("pro.orgs.invite.previewProgress")}
                      </Button>
                    </div>
                  ) : null}
                </>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 13,
                    color: vars.color.fgMuted,
                  }}
                >
                  {t("pro.orgs.invite.claimUnavailable")}
                  <ClaimInfoIcon title={claimTooltip} />
                </div>
              )}
            </div>
          )}

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 14, fontWeight: 500 }}>{t("pro.orgs.inviteToEnroll.role")}</span>
            <select
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
              disabled={loading || rolesLoading || enrollRoles.length === 0}
              style={{
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid var(--color-border, #e5e7eb)",
                fontSize: 14,
              }}
            >
              {enrollRoles.length === 0 ? (
                <option value="">{t("pro.orgs.inviteToEnroll.noEnrollRoles")}</option>
              ) : (
                enrollRoles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.label?.trim() || formatRoleLabel(role.name)}
                  </option>
                ))
              )}
            </select>
          </label>

          {enrollRoles.length === 0 && !rolesLoading && (
            <Callout variant="info">
              No organization roles include both certification enroll and
              take-quiz permissions. Add those permissions to a role first.
            </Callout>
          )}

          <div className={confirmStyles.confirmModalActions}>
            <Button
              color="neutral"
              variant="outline"
              size="md"
              disabled={loading}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              variant="solid"
              size="md"
              loading={loading && !adminConfirmOpen}
              disabled={!canInvite}
              onClick={handleInviteClick}
            >
              Invite
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={adminConfirmOpen}
        setOpen={(next) => {
          if (!loading) setAdminConfirmOpen(next);
        }}
        minWidth="420px"
        maxWidth="480px"
        minHeight="auto"
      >
        <div className={confirmStyles.confirmModalBody}>
          <h3 className={confirmStyles.confirmModalTitle}>
            Invite as {selectedRoleLabel}?
          </h3>
          <p className={confirmStyles.confirmModalText}>
            You are about to invite <strong>{trimmedEmail || "this user"}</strong>{" "}
            as an organization <strong>{selectedRoleLabel}</strong>. Admins have
            full control of the organization. Continue only if this is
            intentional.
          </p>
          <div className={confirmStyles.confirmModalActions}>
            <Button
              color="neutral"
              variant="outline"
              size="md"
              disabled={loading}
              onClick={() => setAdminConfirmOpen(false)}
            >
              Go back
            </Button>
            <Button
              color="danger"
              variant="solid"
              size="md"
              loading={loading}
              onClick={() => void sendInvite()}
            >
              Invite as {selectedRoleLabel}
            </Button>
          </div>
        </div>
      </Modal>

      <MigrationPreviewModal
        open={previewOpen}
        setOpen={setPreviewOpen}
        claimId={previewClaimId}
        accessToken={token}
        showJsonInspectors={false}
      />
    </>
  );
}
